import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { usePatchCatalogItemMutation } from '../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../redux/api/utils/isSuccessfulResult';
import { presaveCreateMessages } from './presaveCreateMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './PresaveCreate.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PresaveDateEditorProps {
  accountId: number;
  itemUuid: string;
  /** `YYYY-MM-DD`, the day of the song now; empty for none. */
  initialDay: string;
  today: string;
  /** Shown when the song has a day, so the artist can keep it. */
  onCancel?: () => void;
  onSaved: () => void;
}

/**
 * The release day of the song (design presave-create a3). It saves on the song with the song page
 * call (`PATCH .../catalog/:uuid { releaseDate }`), so Releases, the Studio and the song page show
 * the same day. A pre-save page needs a day of today or later.
 */
const PresaveDateEditorComponent: React.FC<PresaveDateEditorProps> = ({
  accountId,
  itemUuid,
  initialDay,
  today,
  onCancel,
  onSaved,
}) => {
  const intl = useIntl();
  const [day, setDay] = useState(initialDay >= today ? initialDay : '');
  const [patchCatalogItem, { isLoading, isError }] = usePatchCatalogItemMutation();
  const isPast = day !== '' && day < today;
  const canSave = day !== '' && !isPast && !isLoading;

  const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setDay(event.target.value), []);
  const save = useCallback(() => {
    if (!canSave) {
      return;
    }
    patchCatalogItem({ accountId, itemUuid, releaseDate: day })
      .then((result) => {
        if (isSuccessfulResult(result)) {
          onSaved();
        }
      })
      .catch(console.error);
  }, [accountId, canSave, day, itemUuid, onSaved, patchCatalogItem]);

  return (
    <div className={styles.dateEditor} data-testid="presave-date-editor">
      <label className={styles.dateLabel} htmlFor="presave-release-day">
        {intl.formatMessage(presaveCreateMessages.dateLabel)}
      </label>
      <div className={styles.dateRow}>
        <input
          id="presave-release-day"
          type="date"
          className={styles.dateInput}
          min={today}
          value={day}
          disabled={isLoading}
          onChange={onChange}
        />
        <SMLButton size="sm" color="primary" omitCap disabled={!canSave} isLoading={isLoading} onClick={save}>
          {intl.formatMessage(presaveCreateMessages.dateSave)}
        </SMLButton>
        {onCancel !== undefined && (
          <SMLButton size="sm" outline omitCap disabled={isLoading} onClick={onCancel}>
            {intl.formatMessage(presaveCreateMessages.dateCancel)}
          </SMLButton>
        )}
      </div>
      {isPast && <span className={styles.error}>{intl.formatMessage(presaveCreateMessages.datePast)}</span>}
      {isError && <span className={styles.error}>{intl.formatMessage(presaveCreateMessages.dateFailed)}</span>}
      <span className={styles.hint}>{intl.formatMessage(presaveCreateMessages.dateHint)}</span>
    </div>
  );
};

export const PresaveDateEditor = TypedMemo(PresaveDateEditorComponent);
